import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Palette, Send, Plus, Trash2, Save, ExternalLink } from "lucide-react";

import { getMyCities } from "@/lib/city-teams.functions";
import {
  getMyCityHub,
  upsertCityBranding,
  upsertCityHubEntry,
  deleteCityHubEntry,
  submitCityHubForReview,
  type HubEntry,
  type HubSection,
  type SectionKey,
} from "@/lib/city-hub.functions";

export const Route = createFileRoute("/_authenticated/city-hub")({
  head: () => ({
    meta: [
      { title: "City Hub Editor — What Finland" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: CityHubEditor,
});

const SECTION_LABELS: Record<SectionKey, string> = {
  essentials: "City Essentials",
  outdoor: "Outdoor & Activities",
  highlights_events: "Highlights & Events",
};

function CityHubEditor() {
  const qc = useQueryClient();
  const [cityId, setCityId] = useState<string | null>(null);

  const myCitiesQ = useQuery({
    queryKey: ["city-teams", "my-cities"],
    queryFn: () => getMyCities(),
  });

  useEffect(() => {
    if (!cityId && myCitiesQ.data?.length) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setCityId((myCitiesQ.data[0] as any).id);
    }
  }, [myCitiesQ.data, cityId]);

  const hubQ = useQuery({
    queryKey: ["city-hub", cityId],
    queryFn: () => getMyCityHub({ data: { cityId: cityId! } }),
    enabled: !!cityId,
  });

  const submitMut = useMutation({
    mutationFn: () => submitCityHubForReview({ data: { cityId: cityId! } }),
    onSuccess: (r) => {
      toast.success(`Submitted ${r.submitted} change${r.submitted === 1 ? "" : "s"} for review`);
      qc.invalidateQueries({ queryKey: ["city-hub", cityId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (myCitiesQ.isLoading) {
    return <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  const cities = (myCitiesQ.data ?? []) as Array<{ id: string; name: string; slug: string }>;
  if (!cities.length) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow p-8 text-center">
          <h1 className="text-lg font-semibold">No City Assigned</h1>
          <p className="mt-2 text-sm text-slate-600">Your account isn't linked to a city yet. Ask a What Finland admin to invite you.</p>
        </div>
      </div>
    );
  }

  const hub = hubQ.data;
  const selectedCity = cities.find((c) => c.id === cityId);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center gap-3">
          <div>
            <h1 className="text-lg font-semibold">City Hub</h1>
            <p className="text-xs text-slate-500">Manage the public content for your city.</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <select
              value={cityId ?? ""}
              onChange={(e) => setCityId(e.target.value)}
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-sm"
            >
              {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {selectedCity && (
              <Link
                to="/city-hub/$slug" params={{ slug: selectedCity.slug }}
                className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-slate-200 text-sm hover:bg-slate-50"
              >
                <ExternalLink className="h-3.5 w-3.5" /> Preview
              </Link>
            )}
            <button
              onClick={() => submitMut.mutate()}
              disabled={submitMut.isPending}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
            >
              {submitMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Submit for review
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-8">
        {hubQ.isLoading || !hub ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <>
            <BrandingCard cityId={cityId!} branding={hub.branding} />
            {(["essentials", "outdoor", "highlights_events"] as SectionKey[]).map((key) => {
              const section = hub.sections.find((s) => s.section_key === key);
              if (!section) return null;
              const entries = hub.entries.filter((e) => e.section_id === section.id);
              return <SectionCard key={key} section={section} entries={entries} cityId={cityId!} />;
            })}
          </>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function BrandingCard({ cityId, branding }: { cityId: string; branding: ReturnType<typeof Object> | any }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    banner_url: branding?.banner_url ?? "",
    logo_url: branding?.logo_url ?? "",
    brand_color: branding?.brand_color ?? "#4f46e5",
    tagline: branding?.tagline ?? "",
    seo_title: branding?.seo_title ?? "",
    seo_description: branding?.seo_description ?? "",
  });

  useEffect(() => {
    setForm({
      banner_url: branding?.banner_url ?? "",
      logo_url: branding?.logo_url ?? "",
      brand_color: branding?.brand_color ?? "#4f46e5",
      tagline: branding?.tagline ?? "",
      seo_title: branding?.seo_title ?? "",
      seo_description: branding?.seo_description ?? "",
    });
  }, [branding]);

  const saveMut = useMutation({
    mutationFn: () =>
      upsertCityBranding({
        data: {
          cityId,
          patch: {
            banner_url: form.banner_url || null,
            logo_url: form.logo_url || null,
            brand_color: form.brand_color || null,
            tagline: form.tagline || null,
            seo_title: form.seo_title || null,
            seo_description: form.seo_description || null,
          },
        },
      }),
    onSuccess: () => {
      toast.success("Branding saved as pending review");
      qc.invalidateQueries({ queryKey: ["city-hub", cityId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Palette className="h-5 w-5 text-indigo-600" />
        <h2 className="text-base font-semibold">Custom Branding</h2>
        {branding?.status && (
          <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 capitalize">
            {branding.status}
          </span>
        )}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Banner image URL"><input value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} className={inputCls} placeholder="https://..." /></Field>
        <Field label="Logo URL"><input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className={inputCls} placeholder="https://..." /></Field>
        <Field label="Brand color"><input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-9 w-full rounded border border-slate-200" /></Field>
        <Field label="Tagline"><input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className={inputCls} maxLength={500} /></Field>
        <Field label="SEO title"><input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className={inputCls} maxLength={160} /></Field>
        <Field label="SEO description"><input value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} className={inputCls} maxLength={320} /></Field>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-60"
        >
          {saveMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save branding
        </button>
      </div>
    </section>
  );
}

function SectionCard({ section, entries, cityId }: { section: HubSection; entries: HubEntry[]; cityId: string }) {
  const [adding, setAdding] = useState(false);
  return (
    <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-base font-semibold">{SECTION_LABELS[section.section_key as SectionKey] ?? section.title}</h2>
        <button
          onClick={() => setAdding(true)}
          className="ml-auto inline-flex items-center gap-1 text-sm px-3 h-8 rounded-md border border-slate-200 hover:bg-slate-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add entry
        </button>
      </div>
      {section.subtitle && <p className="text-xs text-slate-500 -mt-2 mb-4">{section.subtitle}</p>}
      <div className="grid gap-3 md:grid-cols-2">
        {entries.length === 0 && !adding && (
          <p className="text-sm text-slate-500 col-span-full">No entries yet. Add your first item.</p>
        )}
        {entries.map((entry) => (
          <EntryEditor key={entry.id} entry={entry} sectionId={section.id} cityId={cityId} />
        ))}
        {adding && (
          <EntryEditor
            entry={null}
            sectionId={section.id}
            cityId={cityId}
            onDone={() => setAdding(false)}
          />
        )}
      </div>
    </section>
  );
}

function EntryEditor({
  entry, sectionId, cityId, onDone,
}: {
  entry: HubEntry | null; sectionId: string; cityId: string; onDone?: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: entry?.title ?? "",
    description: entry?.description ?? "",
    url: entry?.url ?? "",
    address: entry?.address ?? "",
    image_url: entry?.image_url ?? "",
    icon: entry?.icon ?? "",
    curator_note: entry?.curator_note ?? "",
  });

  const saveMut = useMutation({
    mutationFn: () =>
      upsertCityHubEntry({
        data: {
          entryId: entry?.id ?? null,
          sectionId,
          cityId,
          kind: entry?.kind ?? "custom",
          patch: {
            title: form.title || null,
            description: form.description || null,
            url: form.url || null,
            address: form.address || null,
            image_url: form.image_url || null,
            icon: form.icon || null,
            curator_note: form.curator_note || null,
          },
        },
      }),
    onSuccess: () => {
      toast.success(entry ? "Saved as draft" : "Entry added as draft");
      qc.invalidateQueries({ queryKey: ["city-hub", cityId] });
      if (!entry) onDone?.();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: () => deleteCityHubEntry({ data: { entryId: entry!.id, cityId } }),
    onSuccess: () => {
      toast.success("Entry removed");
      qc.invalidateQueries({ queryKey: ["city-hub", cityId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="rounded-xl border border-slate-200 p-4 space-y-2 bg-slate-50/40">
      <div className="flex items-center gap-2">
        {entry && (
          <span className={`text-[10px] uppercase font-semibold tracking-wide px-1.5 py-0.5 rounded ${statusPillCls(entry.status)}`}>
            {entry.status}
          </span>
        )}
        <span className="text-xs text-slate-500">{entry?.kind ?? "custom"}</span>
        <button
          onClick={() => (entry ? delMut.mutate() : onDone?.())}
          className="ml-auto text-slate-400 hover:text-rose-600"
          title={entry ? "Delete entry" : "Cancel"}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" className={inputCls} />
      <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" rows={2} className={inputCls} />
      <div className="grid grid-cols-2 gap-2">
        <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="URL" className={inputCls} />
        <input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="Icon (e.g. parking)" className={inputCls} />
      </div>
      <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" className={inputCls} />
      <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL" className={inputCls} />
      {entry?.review_note && (
        <p className="text-xs text-amber-700 bg-amber-50 rounded p-2">Reviewer note: {entry.review_note}</p>
      )}
      <button
        onClick={() => saveMut.mutate()}
        disabled={saveMut.isPending}
        className="w-full inline-flex items-center justify-center gap-1.5 h-8 rounded-md bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-60"
      >
        {saveMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
        {entry ? "Save draft" : "Add entry"}
      </button>
    </div>
  );
}

const inputCls =
  "w-full h-9 rounded-md border border-slate-200 bg-white px-2.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium text-slate-600">{label}</span>
      {children}
    </label>
  );
}

function statusPillCls(status: string) {
  switch (status) {
    case "published": return "bg-emerald-100 text-emerald-800";
    case "pending":   return "bg-amber-100 text-amber-800";
    case "rejected":  return "bg-rose-100 text-rose-800";
    default:          return "bg-slate-200 text-slate-700";
  }
}
